import { useTranslation } from 'react-i18next'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import type { BaseShopModuleModel, SimpleGroupResponse, WalletTypeResponse } from '@/api/model'
import { ShopModuleCard } from './ShopModuleCard'
import { hasDetailPage, resolveGroupNames } from '../lib'
import { resolveWalletTypeDisplay } from '@/lib/walletTypes'

const GRID_CLASS = 'grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4'

interface Props {
  /** Already sorted and filtered by the page. */
  modules: BaseShopModuleModel[]
  walletTypes: WalletTypeResponse[]
  groups: SimpleGroupResponse[]
  isLoading: boolean
  isError: boolean
  /** true = a search is active (only affects the empty text). */
  isFiltered?: boolean
  /** Card click — only wired for types that actually have a detail page. */
  onOpen: (module: BaseShopModuleModel) => void
  /** Pencil click — the card itself decides whether the user may edit. */
  onEdit: (module: BaseShopModuleModel) => void
}

/**
 * Tile grid of all shop modules. No drag & drop: the order is the business `priority`,
 * not a UI state, and there is no reorder endpoint.
 */
export function ShopModuleGrid({ modules, walletTypes, groups, isLoading, isError, isFiltered, onOpen, onEdit }: Props) {
  const { t } = useTranslation('shop-modules')

  return (
    <LoaderGeneral
      isLoading={isLoading}
      isError={isError}
      isEmpty={modules.length === 0}
      emptyMessage={isFiltered ? t('grid.noResults') : t('grid.empty')}
    >
      <div className={GRID_CLASS}>
        {modules.map((module) => (
          <ShopModuleCard
            key={module.id}
            module={module}
            walletType={resolveWalletTypeDisplay(module.walletTypeId, walletTypes)}
            groupNames={resolveGroupNames(module.assignedGroups, groups)}
            onOpen={hasDetailPage(module.type) ? onOpen : undefined}
            onEdit={onEdit}
          />
        ))}
      </div>
    </LoaderGeneral>
  )
}
